import React, { useState, useRef, useEffect } from "react";
import "./Sidebar.css";
import appnameImg from "../assets/appfullname.png";
import {
    FaThLarge,
    FaRegFileAlt,
    FaTasks,
    FaFolderOpen,
    FaProjectDiagram,
    FaUsers,
    FaBars,
    FaTimes,
    FaUser,
} from "react-icons/fa";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { GrPlan } from "react-icons/gr";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { gsap } from "gsap";
import ChangePasswordModal from "./ChangePasswordModal";
import axiosInstance from "../api/axiosInstance";
import { usePostHog } from "posthog-js/react";
import { logout } from "../features/auth/authSlice";
import { persistor } from "../lib/store";
import { toast } from "react-toastify";

const sidebarLinks = [
    /*{ icon: <FaThLarge />, label: "Dashboard" },*/
    { icon: <FaRegFileAlt />, label: "Summarize" },
    { icon: <FaTasks />, label: "My Task" },
    { icon: <FaFolderOpen />, label: "Deputed Task" },
    { icon: <FaProjectDiagram />, label: "Project" },
    { icon: <FaUsers />, label: "Groups" },
    {
        icon: <FaRegFileAlt />,
        label: "Daily Checklist",
        submenu: [
            { icon: <GrPlan />, label: "Planning", link: "/yesno?tab=planning" },
            { icon: <IoMdCheckmarkCircleOutline />, label: "Execution", link: "/yesno?tab=execution" },
        ],
    },
];

const getLinkPath = (label) => {
    switch (label) {
        case "Dashboard": return "/dashboard";
        case "Summarize": return "/summarize";
        case "My Task": return "/mytask";
        case "Deputed Task": return "/task";
        case "Groups": return "/groups";
        case "Project": return "/project";
        case "Roles": return "/roles";
        case "Daily Checklist": return "/yesno";
        default: return "";
    }
};

const Sidebar = ({ sidebarOpen, setSidebarOpen, isMobile }) => {
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState(null);
    const [submenuPositions, setSubmenuPositions] = useState({});
    const userMenuRef = useRef(null);
    const dropdownRef = useRef(null);
    const submenuRefs = useRef({});
    const menuItemRefs = useRef({});
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector((state) => state.auth.user) ?? {};
    const [showChangePassword, setShowChangePassword] = useState(false);
    const posthog = usePostHog();
    const dispatch = useDispatch();

    // Identify user in PostHog once user is available from Redux
    useEffect(() => {
        if (user?.userId) {
            posthog.identify(user.userId, {
                email: user.email,
                departmentId: user.department_id,
            });
        }
    }, [user, posthog]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setUserMenuOpen(false);
            }
            if (openSubmenu && submenuRefs.current[openSubmenu]) {
                const submenuEl = submenuRefs.current[openSubmenu];
                if (submenuEl && !submenuEl.contains(event.target)) {
                    const clickedElement = event.target.closest(".sidebar-link");
                    const isMenuItemClick = clickedElement?.textContent?.includes(openSubmenu);
                    if (!isMenuItemClick) setOpenSubmenu(null);
                }
            }
        }
        if (userMenuOpen || openSubmenu) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [userMenuOpen, openSubmenu]);


    useEffect(() => {
        if (userMenuOpen && dropdownRef.current && sidebarOpen) {
            const options = Array.from(
                dropdownRef.current.querySelectorAll(".sidebar-user-dropdown-option")
            );
            gsap.fromTo(
                dropdownRef.current,
                { y: 32, scale: 0.92, opacity: 0 },
                { y: 0, scale: 1, opacity: 1, duration: 0.32, ease: "back.out(1.7)" }
            );
            gsap.fromTo(
                options,
                { y: 18, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.22, stagger: 0.07, ease: "power2.out" },
                "-=0.18"
            );
        }
    }, [userMenuOpen, sidebarOpen]);

    const handleLogout = async () => {
        try {
            setUserMenuOpen(false);
            await axiosInstance.post("api/auth/logout");
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            dispatch(logout());
            await persistor.purge();
            navigate("/sign-in");
        }
    };

    const handleLinkClick = (label) => {
        const link = sidebarLinks.find((l) => l.label === label);
        if (link && link.submenu) {
            if (!sidebarOpen && menuItemRefs.current[label]) {
                const rect = menuItemRefs.current[label].getBoundingClientRect();
                setSubmenuPositions((prev) => ({ ...prev, [label]: { top: rect.top } }));
            }
            setOpenSubmenu((prev) => (prev === label ? null : label));
            return;
        }
        navigate(getLinkPath(label));
        if (isMobile) setSidebarOpen(false);
    };

    const handleChangePassword = async ({ oldPassword, newPassword, confirmPassword }) => {
        try {
            if (!user || !user.userId) {
                toast.error("User information not available. Please refresh the page.");
                return;
            }
            const { data } = await axiosInstance.post("api/auth/change-password", {
                userId: user.userId, oldPassword, newPassword, confirmPassword
            });
            if (data.success) {
                toast.success("Password changed successfully!");
                setShowChangePassword(false);
            } else {
                toast.error(data.message || "Failed to change password.");
            }
        } catch (err) {
            toast.error(err.message || "Failed to change password.");
        }
    };

    return (
        <>
            <aside className={`sidebar${sidebarOpen ? " sidebar-open" : " collapsed"}${isMobile ? " mobile" : ""}`}>
                <div className="sidebar-content">
                    <div className="sidebar-top-row">
                        {sidebarOpen && (
                            <div className="sidebar-header">
                                <img src={appnameImg} alt="App Name" className="sidebar-app-name" />
                            </div>
                        )}
                        {!isMobile && (
                            <button className="sidebar-hamburger" onClick={() => setSidebarOpen((open) => !open)}>
                                <FaBars />
                            </button>
                        )}
                        {isMobile && sidebarOpen && (
                            <button className="sidebar-hamburger" onClick={() => setSidebarOpen(false)} style={{ marginLeft: "auto" }}>
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <nav className="sidebar-nav">
                        {sidebarLinks.map((link) => (
                            <div
                                key={link.label}
                                ref={(el) => { if (link.submenu) menuItemRefs.current[link.label] = el; }}
                                style={{ position: "relative", zIndex: openSubmenu === link.label ? 100 : "auto" }}
                            >
                                <div
                                    className={`sidebar-link${location.pathname === getLinkPath(link.label) ? " active" : ""}`}
                                    onClick={() => handleLinkClick(link.label)}
                                    title={!sidebarOpen ? link.label : ""}
                                >
                                    <span className="sidebar-icon">{link.icon}</span>
                                    {sidebarOpen && <span className="sidebar-label">{link.label}</span>}
                                    {link.submenu && (
                                        <span style={{
                                            marginLeft: "auto", fontSize: "0.75rem", transition: "transform 0.2s",
                                            transform: openSubmenu === link.label ? "rotate(180deg)" : "rotate(0)",
                                            display: sidebarOpen ? "inline" : "none",
                                        }}>▼</span>
                                    )}
                                </div>

                                {link.submenu && openSubmenu === link.label && (
                                    <div
                                        ref={(el) => { submenuRefs.current[link.label] = el; }}
                                        className={`submenu-dropdown ${sidebarOpen ? "expanded" : "collapsed"}`}
                                        style={{ top: !sidebarOpen ? `${submenuPositions[link.label]?.top || 0}px` : undefined }}
                                    >
                                        {link.submenu.map((subItem, index) => (
                                            <button
                                                key={index}
                                                className="submenu-item"
                                                onClick={() => {
                                                    navigate(subItem.link);
                                                    if (!sidebarOpen) setOpenSubmenu(null);
                                                    if (isMobile) setSidebarOpen(false);
                                                }}
                                                title={!sidebarOpen ? subItem.label : ""}
                                            >
                                                {subItem.icon} {subItem.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>

                    <div
                        className="sidebar-user"
                        ref={userMenuRef}
                        onClick={() => setUserMenuOpen((open) => !open)}
                        style={{ cursor: "pointer", position: "relative" }}
                        title={!sidebarOpen ? (user.FirstName || user.email || "User") : ""}
                    >
                        <FaUser />
                        {sidebarOpen && (
                            <div>
                                <div className="sidebar-user-name">{user.FirstName || user.email || "User"}</div>
                            </div>
                        )}
                        {userMenuOpen && (
                            <div
                                ref={dropdownRef}
                                className={`user-menu-dropdown ${sidebarOpen ? "expanded" : "collapsed"}`}
                                style={!sidebarOpen && userMenuRef.current ? (() => {
                                    const rect = userMenuRef.current.getBoundingClientRect();
                                    return { bottom: (window.innerHeight - rect.bottom) + "px" };
                                })() : undefined}
                            >
                                {sidebarOpen && <div className="user-menu-arrow" />}
                                <button
                                    className={!sidebarOpen ? "user-menu-item" : "sidebar-dropdown-btn sidebar-user-dropdown-option"}
                                    onClick={() => { setUserMenuOpen(false); navigate("/myprofile"); }}
                                >
                                    My Profile
                                </button>
                                <button
                                    className={!sidebarOpen ? "user-menu-item" : "sidebar-dropdown-btn sidebar-user-dropdown-option"}
                                    onClick={() => { setUserMenuOpen(false); setShowChangePassword(true); }}
                                >
                                    Change Password
                                </button>
                                <button
                                    className={!sidebarOpen ? "user-menu-item" : "sidebar-dropdown-btn sidebar-dropdown-btn-logout sidebar-user-dropdown-option"}
                                    onClick={handleLogout}
                                >
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>
            <ChangePasswordModal
                open={showChangePassword}
                onClose={() => setShowChangePassword(false)}
                onSave={handleChangePassword}
            />
        </>
    );
};

export default Sidebar;
